import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Checkbox, DarkSelect, Input } from "@/components/ui";

export interface ServerDetails {
  id: number;
  name: string;
  host: string;
  port: number;
  server_type: string;
  ssh_host: string | null;
  ssh_port: number;
  ssh_user: string | null;
  ssh_key_path: string | null;
  ad_tag: string | null;
  test_secret: string | null;
  public_secret: string | null;
  cluster_id: number | null;
  geo_allow_countries: string | null;
  max_users: number;
  max_connections: number;
  is_active: boolean;
  active_users: number;
  created_at: string;
  // Premium tier + bridge параметры (phase 2 — backend начал отдавать).
  tier?: "standard" | "premium";
  bridge_host?: string | null;
  bridge_ssh_host?: string | null;
  bridge_ssh_user?: string | null;
  bridge_ssh_key_path?: string | null;
  bridge_port?: number;
  exit_port?: number;
  cloak_domain?: string | null;
}

interface FormState {
  name: string;
  host: string;
  port: string;
  server_type: string;
  ssh_host: string;
  ssh_port: string;
  ssh_user: string;
  ssh_key_path: string;
  ad_tag: string;
  public_secret: string;
  geo_allow_countries: string;
  max_users: string;
  max_connections: string;
  is_active: boolean;
  tier: "standard" | "premium";
  bridge_host: string;
  bridge_ssh_host: string;
  bridge_ssh_user: string;
  bridge_ssh_key_path: string;
  bridge_port: string;
  exit_port: string;
  cloak_domain: string;
}

const EMPTY: FormState = {
  name: "",
  host: "",
  port: "443",
  server_type: "paid",
  ssh_host: "",
  ssh_port: "22",
  ssh_user: "",
  ssh_key_path: "",
  ad_tag: "",
  public_secret: "",
  geo_allow_countries: "",
  max_users: "100",
  max_connections: "60000",
  is_active: true,
  tier: "standard",
  bridge_host: "",
  bridge_ssh_host: "",
  bridge_ssh_user: "",
  bridge_ssh_key_path: "",
  bridge_port: "443",
  exit_port: "8444",
  cloak_domain: "",
};

function fromServer(s: ServerDetails): FormState {
  return {
    name: s.name,
    host: s.host,
    port: String(s.port),
    server_type: s.server_type,
    ssh_host: s.ssh_host ?? "",
    ssh_port: String(s.ssh_port ?? 22),
    ssh_user: s.ssh_user ?? "",
    ssh_key_path: s.ssh_key_path ?? "",
    ad_tag: s.ad_tag ?? "",
    public_secret: s.public_secret ?? "",
    geo_allow_countries: s.geo_allow_countries ?? "",
    max_users: String(s.max_users),
    max_connections: String(s.max_connections),
    is_active: s.is_active,
    tier: (s.tier ?? "standard") as "standard" | "premium",
    bridge_host: s.bridge_host ?? "",
    bridge_ssh_host: s.bridge_ssh_host ?? "",
    bridge_ssh_user: s.bridge_ssh_user ?? "",
    bridge_ssh_key_path: s.bridge_ssh_key_path ?? "",
    bridge_port: String((s as any).bridge_port ?? 443),
    exit_port: String((s as any).exit_port ?? 8444),
    cloak_domain: s.cloak_domain ?? "",
  };
}

function randomSecret(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

interface Props {
  mode: "create" | "edit";
  initial?: ServerDetails;
  onSaved: (server: ServerDetails) => void;
  onCancel: () => void;
}

export function ServerForm({ mode, initial, onSaved, onCancel }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(initial ? fromServer(initial) : EMPTY);

  useEffect(() => {
    if (initial) setForm(fromServer(initial));
  }, [initial]);

  const save = useMutation({
    mutationFn: async () => {
      const isAd = form.server_type === "ad";
      const isPremium = form.tier === "premium";
      const body = {
        name: form.name,
        host: form.host,
        port: Number(form.port) || 443,
        server_type: form.server_type,
        ssh_host: form.ssh_host || null,
        ssh_port: Number(form.ssh_port) || 22,
        ssh_user: form.ssh_user || null,
        ssh_key_path: form.ssh_key_path || null,
        ad_tag: form.ad_tag || null,
        public_secret: isAd ? (form.public_secret || null) : null,
        geo_allow_countries: form.geo_allow_countries.trim() || null,
        max_users: Number(form.max_users) || 100,
        max_connections: Number(form.max_connections) || 60000,
        is_active: form.is_active,
        // Premium-блок отправляем всегда (на backend tier валидируется
        // {"standard","premium"}), bridge_* — только для premium и
        // только если заполнено, чтобы не затирать null'ом при правке.
        tier: form.tier,
        bridge_host: isPremium ? (form.bridge_host || null) : null,
        bridge_ssh_host: isPremium ? (form.bridge_ssh_host || null) : null,
        bridge_ssh_user: isPremium ? (form.bridge_ssh_user || null) : null,
        bridge_ssh_key_path: isPremium ? (form.bridge_ssh_key_path || null) : null,
        bridge_port: isPremium ? (Number(form.bridge_port) || 443) : 443,
        exit_port: isPremium ? (Number(form.exit_port) || 8444) : 8444,
        // Cloak — пустая строка трактуется как «использовать глобальный
        // дефолт из Settings». Для non-premium не отправляем (хотя
        // backend всё равно игнорирует — поле полезно только zig).
        cloak_domain: isPremium ? (form.cloak_domain.trim() || null) : null,
      };
      if (mode === "edit" && initial) {
        return await api<ServerDetails>(`/api/admin/servers/${initial.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      }
      return await api<ServerDetails>(`/api/admin/servers`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["servers"] });
      onSaved(result);
    },
  });

  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const isAd = form.server_type === "ad";
  const isPremium = form.tier === "premium";
  // Premium с bridge_host обязан иметь bridge_ssh_host (или = bridge_host)
  // для управления — но это не блокирующая валидация, просто info.
  const invalid =
    !form.name.trim() ||
    !form.host.trim() ||
    (isPremium && form.bridge_host.trim() !== "" && !form.bridge_ssh_host.trim() && false);
  // Note: SSH-host для bridge можно оставить пустым — backend подставит = bridge_host.

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="имя">
          <Input value={form.name} onChange={(e) => upd("name", e.target.value)} autoFocus />
        </Field>
        <Field label="тип">
          <DarkSelect
            value={form.server_type}
            onChange={(v) => upd("server_type", v)}
            options={[
              { value: "paid", label: "paid" },
              { value: "test", label: "test" },
              { value: "ad", label: "ad" },
            ]}
          />
        </Field>
        <Field label="тариф (tier)">
          <DarkSelect
            value={form.tier}
            onChange={(v) => upd("tier", v as "standard" | "premium")}
            options={[
              { value: "standard", label: "🏠 Standard", hint: "alexbers / docker" },
              { value: "premium", label: "👑 Premium", hint: "mtproto.zig + bridge" },
            ]}
          />
        </Field>
        <Field label="host">
          <Input value={form.host} onChange={(e) => upd("host", e.target.value)} />
        </Field>
        <Field label="port">
          <Input value={form.port} onChange={(e) => upd("port", e.target.value)} />
        </Field>
        <Field label="max_users">
          <Input value={form.max_users} onChange={(e) => upd("max_users", e.target.value)} />
        </Field>
        <Field label="max_connections (per worker)">
          <Input value={form.max_connections} onChange={(e) => upd("max_connections", e.target.value)} />
        </Field>
        <Field label="ad_tag">
          <Input
            value={form.ad_tag}
            placeholder={isAd ? "выдаётся @MTProxybot" : "необязательно"}
            onChange={(e) => upd("ad_tag", e.target.value)}
          />
        </Field>
      </div>

      {isAd && (
        <div className="border-t border-zinc-800 pt-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
            Публичный secret
          </div>
          <div className="flex gap-2 items-stretch">
            <Input
              value={form.public_secret}
              placeholder="32 hex символа"
              onChange={(e) => upd("public_secret", e.target.value)}
              className="flex-1 font-mono"
            />
            <Button
              variant="ghost"
              onClick={() => upd("public_secret", randomSecret())}
              type="button"
            >
              Сгенерировать
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Один secret на весь рекламный сервер — так же выглядит в публичной ссылке. Docker-образ telegrammessenger/proxy поднимается с этим значением.
          </p>
        </div>
      )}

      <div className="border-t border-zinc-800 pt-4">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
          {isPremium ? "SSH к exit-ноде (mtproto.zig)" : "SSH"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="ssh_host">
            <Input
              value={form.ssh_host}
              placeholder="по умолчанию = host"
              onChange={(e) => upd("ssh_host", e.target.value)}
            />
          </Field>
          <Field label="ssh_port">
            <Input value={form.ssh_port} onChange={(e) => upd("ssh_port", e.target.value)} />
          </Field>
          <Field label="ssh_user">
            <Input
              value={form.ssh_user}
              placeholder="root"
              onChange={(e) => upd("ssh_user", e.target.value)}
            />
          </Field>
          <Field label="ssh_key_path">
            <Input
              value={form.ssh_key_path}
              placeholder="по умолчанию из .env"
              onChange={(e) => upd("ssh_key_path", e.target.value)}
            />
          </Field>
        </div>
      </div>

      {isPremium && (
        <div className="border-t border-zinc-800 pt-4 bg-amber-950/20 -mx-4 px-4 pb-4 rounded">
          <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">
            Bridge-нода (необязательно) — Premium double-hop
          </div>
          <p className="text-xs text-zinc-400 mb-3">
            <b>Минимальный premium</b> = один сервер с mtproto.zig + ee-ссылкой
            (FakeTLS cloak wb.ru). Bridge не нужен — оставь все поля ниже
            пустыми, UI этой секцией просто игнорируется.
            <br />
            <b>Double-hop premium</b> = bridge в РФ-сети (Yandex.Cloud /
            VK Cloud / Selectel) + exit за границей. Bridge — это «лицо»
            прокси (host наверху должен совпадать с bridge_host), он
            форвардит TCP через xray на exit_host:exit_port. Используется
            для whitelist-обхода (МТС/Билайн агрессивные).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="bridge_host (= host выше)">
              <Input
                value={form.bridge_host}
                placeholder="например 111.88.156.136"
                onChange={(e) => upd("bridge_host", e.target.value)}
              />
            </Field>
            <Field label="bridge_port">
              <Input
                value={form.bridge_port}
                placeholder="443"
                onChange={(e) => upd("bridge_port", e.target.value)}
              />
            </Field>
            <Field label="bridge_ssh_host">
              <Input
                value={form.bridge_ssh_host}
                placeholder="по умолчанию = bridge_host"
                onChange={(e) => upd("bridge_ssh_host", e.target.value)}
              />
            </Field>
            <Field label="bridge_ssh_user">
              <Input
                value={form.bridge_ssh_user}
                placeholder="root / tando / ..."
                onChange={(e) => upd("bridge_ssh_user", e.target.value)}
              />
            </Field>
            <Field label="bridge_ssh_key_path">
              <Input
                value={form.bridge_ssh_key_path}
                placeholder="по умолчанию = ssh_key_path или из .env"
                onChange={(e) => upd("bridge_ssh_key_path", e.target.value)}
              />
            </Field>
            <Field label="exit_port (mtproto.zig)">
              <Input
                value={form.exit_port}
                placeholder="8444"
                onChange={(e) => upd("exit_port", e.target.value)}
              />
            </Field>
            <Field label="cloak_domain (FakeTLS SNI)">
              <Input
                value={form.cloak_domain}
                placeholder="пусто = глобальный (cloudflare.com)"
                onChange={(e) => upd("cloak_domain", e.target.value)}
              />
            </Field>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            <b>Cloak-домен</b> — какой SNI прокси показывает DPI. Дефолт из
            Settings (<code>cloudflare.com</code>). Заполни здесь только если
            для этого сервера нужно другое значение (например для US-инфры
            <code>wb.ru</code> не подходит — её dns не резолвится в США).
          </p>
        </div>
      )}

      <div className="border-t border-zinc-800 pt-4">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
          Гео-фильтр
        </div>
        <div className="flex gap-2 items-stretch">
          <Input
            value={form.geo_allow_countries}
            placeholder="RU,BY,UA,KZ,DE,FR,IT,GB,NL,PL"
            onChange={(e) => upd("geo_allow_countries", e.target.value)}
            className="flex-1 font-mono"
          />
          <Button
            variant="ghost"
            type="button"
            onClick={() =>
              upd(
                "geo_allow_countries",
                "RU,BY,UA,KZ,DE,FR,IT,GB,NL,BE,ES,PT,SE,FI,NO,DK,PL,CZ,SK,HU,RO,BG,GR,AT,CH,IE,EE,LV,LT",
              )
            }
          >
            Пресет RU+EU
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Коды стран через запятую. Пусто = доступ со всего мира. Список
          сохраняется в БД; чтобы <b>применить</b> на сервере — открой карточку
          сервера и нажми «Применить фильтр» в блоке «Гео-фильтр».
        </p>
      </div>

      <Checkbox
        checked={form.is_active}
        onChange={(v) => upd("is_active", v)}
        label="сервер активен"
      />

      {save.isError && (
        <div className="text-xs text-red-400">{(save.error as Error).message}</div>
      )}

      <div className="flex gap-3 pt-2 justify-end">
        <Button variant="ghost" onClick={onCancel} disabled={save.isPending}>
          Отмена
        </Button>
        <Button
          variant="primary"
          disabled={save.isPending || invalid}
          onClick={() => save.mutate()}
        >
          {save.isPending
            ? "Сохраняем…"
            : mode === "create"
              ? "Создать сервер"
              : "Сохранить"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
